import { useEffect, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, setToken, type PublicConfig, type User } from "../lib/api";

export function Auth({ onAuthed }: { onAuthed: (u: User) => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState("");
  const [cfg, setCfg] = useState<PublicConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Learn the instance's registration policy so we show the right fields.
  useEffect(() => {
    api.config().then(setCfg).catch(() => setCfg(null));
  }, []);

  const requireInvite = cfg?.requireInvite ?? true;
  const allowRegistration = cfg?.allowRegistration ?? true;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { token, user } =
        mode === "login"
          ? await api.login(username.trim(), password)
          : await api.register(username.trim(), password, invite.trim() || undefined);
      setToken(token);
      onAuthed(user);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function keyLogin() {
    setError(null);
    setBusy(true);
    try {
      const { options, challengeId } = await api.webauthnLoginOptions(username.trim());
      const authResp = await startAuthentication({ optionsJSON: options });
      const { token, user } = await api.webauthnLoginVerify(challengeId, authResp);
      setToken(token);
      onAuthed(user);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const registering = mode === "register";
  return (
    <div className="center">
      <form className="card" onSubmit={submit}>
        <h2>whitenoise</h2>
        <p className="muted" style={{ marginTop: -8 }}>
          {registering ? "Create an account." : "Sign in to your account."} Your message
          passphrases are never sent here.
        </p>
        <div className="field">
          <label>Username</label>
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus autoComplete="username" />
        </div>
        <div className="field">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={registering ? "new-password" : "current-password"}
          />
        </div>
        {registering && requireInvite && (
          <div className="field">
            <label>Invite code</label>
            <input
              value={invite}
              onChange={(e) => setInvite(e.target.value)}
              placeholder="required — ask the instance admin"
            />
          </div>
        )}
        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "…" : registering ? "Register" : "Sign in"}
        </button>
        {!registering && (
          <button
            type="button"
            className="ghost"
            onClick={keyLogin}
            disabled={busy || !username.trim()}
            style={{ width: "100%", marginTop: 8 }}
            title="Sign in by tapping a registered security key"
          >
            🔑 Sign in with a security key
          </button>
        )}
        {allowRegistration ? (
          <p className="muted" style={{ marginBottom: 0, marginTop: 12 }}>
            {registering ? "Have an account? " : "No account? "}
            <button
              type="button"
              className="linkbtn"
              onClick={() => {
                setMode(registering ? "login" : "register");
                setError(null);
              }}
            >
              {registering ? "Sign in" : "Register"}
            </button>
          </p>
        ) : (
          !registering && (
            <p className="muted" style={{ marginBottom: 0, marginTop: 12 }}>
              Registration is closed on this instance.
            </p>
          )
        )}
      </form>
    </div>
  );
}
